import { Download, FileDownloadDone, ListAlt } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { ALL_REVIEW_STATUSES, backend, CaseClientStatus } from "Backend";
import PSelect from "components/PSelect";
import UserAutoComplete from "components/UserAutocomplete";
import { FC, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import RequestStatus from "redux/slices/RequestStatus";
import {
  CaseFilterQueryParam,
  CaseFilterQueryParams,
  fetchClientLensCases,
  parseCaseFilterQueryParams,
  selectClientLens,
  selectClientLensAssignees,
  selectClientLensCases,
  selectClientLensCasesStatus,
  selectClientLensCasesTotal,
  setCaseFilters,
} from "redux/slices/clientLens";
import { selectEnableFirmFeatureSet } from "redux/slices/session";
import theme from "theme";
import CaseTable from "../CaseTable";
import CaseTableFilterEnhanced from "../CaseTableFilter";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";
import { useSnackbar } from "notistack";
import triggerDownload from "helpers/triggerDownload";
import { ManagementCard } from "routes/lenses/lens/admin/management/ManagementCard";
import { useFirmsByLensId } from "helpers/hooks/useAllowedLenses";
import { GetLensCasesQueryVariables } from "generated/graphql";
import { setEquals } from "helpers/coll";

interface ViewCasesDialogProps {
  lensId: number;
}

const showAllReviewStatusesValue = "";

export const ViewCasesDialog: FC<ViewCasesDialogProps> = ({ lensId }) => {
  const { searchParams, updateParams } = useSearchParamsWithUpdate();
  const caseFilterParams = useMemo(() => parseCaseFilterQueryParams(searchParams), [searchParams]);
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const clientLens = useAppSelector(selectClientLens);
  const assignees = useAppSelector(selectClientLensAssignees);
  const unassignedUser = {
    id: -1,
    firstName: "Unassigned",
    lastName: "",
    email: "",
  };
  const optionsForUserAutocomplete = [unassignedUser].concat(assignees);
  const cases = useAppSelector(selectClientLensCases);
  const casesTotal = useAppSelector(selectClientLensCasesTotal);
  const casesStatus = useAppSelector(selectClientLensCasesStatus);
  const firms = useFirmsByLensId(lensId);
  const selectedNamedUser = optionsForUserAutocomplete.find((u) => u.id === caseFilterParams.assigneeId);
  const enableFirmFeatures = useAppSelector(selectEnableFirmFeatureSet);
  const [includePropertiesCheckbox, setIncludePropertiesCheckbox] = useState<boolean>(false);
  const [caseDownloadMenuAnchorEl, setCaseDownloadMenuAnchorEl] = useState<null | HTMLElement>(null);
  const { enqueueSnackbar } = useSnackbar();

  const caseStatusForBackend = caseFilterParams.caseStatus === "all" ? undefined : caseFilterParams.caseStatus;

  const fetchCases = (params: CaseFilterQueryParams) => {
    const filter: GetLensCasesQueryVariables = {
      lensId,
      textQuery: params.textQuery,
      assigneeId: params.unassigned ? undefined : params.assigneeId,
      caseStatus: caseStatusForBackend,
      reviewStatuses: params.reviewStatuses,
      plaintiffFirmId: params.plaintiffFirmId,
      offset: params.page * params.rowsPerPage,
      limit: params.rowsPerPage,
      minPageCount: params.hasDocuments ? 1 : undefined,
      maxPageCount: params.hasDocuments === false ? 0 : undefined,
      unassigned: params.unassigned,
    };
    dispatch(setCaseFilters(searchParams.toString()));
    return dispatch(fetchClientLensCases(filter));
  };

  const handleCaseDownload = async () => {
    const { fileName, data } = await backend.downloadCaseList({
      lensId: lensId,
      includeProperties: includePropertiesCheckbox,
      filters: {
        offset: 0,
        limit: casesTotal,
        textQuery: caseFilterParams.textQuery,
        caseStatus: caseStatusForBackend,
        reviewStatuses: caseFilterParams.reviewStatuses,
        assigneeId: caseFilterParams.assigneeId,
        plaintiffFirmId: caseFilterParams.plaintiffFirmId,
      },
    });
    triggerDownload(
      new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      fileName
    );
  };

  const handleCaseDownloadClick = () => {
    setCaseDownloadMenuAnchorEl(null);
    handleCaseDownload()
      .then(() => enqueueSnackbar("Case list downloaded successfully", { variant: "success" }))
      .catch(() => enqueueSnackbar("Error downloading case list ", { variant: "error" }));
  };

  const addFilter = (key: string, value: string | string[] | undefined) => {
    updateParams([
      { key, value },
      // always reset to page 1 when adding a filter in case the new filter reduces the number of pages
      { key: "page", value: "1" },
    ]);
  };

  useEffect(() => {
    const thunk = fetchCases(caseFilterParams);
    return () => thunk.abort();
  }, [lensId, caseFilterParams]);

  // The counts only apply to active cases
  const shouldShowReviewStatusCounts = caseFilterParams.caseStatus === CaseClientStatus.Active;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "grey.50",
        minHeight: 0,
        pt: 2,
      }}>
      <Card>
        <CardContent sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box
              sx={{
                maxWidth: "500px",
                width: "100%",
              }}>
              <CaseTableFilterEnhanced
                label="Search by Claimant Name or Case ID"
                selection={caseFilterParams.textQuery || ""}
                setSelection={(value) => addFilter(CaseFilterQueryParam.SEARCH, value)}
                lensId={lensId}
              />
            </Box>

            {!isMobile && (
              <>
                {enableFirmFeatures && (
                  <FormControl size="small" sx={{ width: 600 }}>
                    <InputLabel variant="outlined">Firm</InputLabel>
                    <PSelect
                      data-pendo="case-table-filter-firm"
                      label="Firm"
                      value={caseFilterParams.plaintiffFirmId || ""}
                      onChange={(evt) => {
                        const v = evt.target.value as string;
                        addFilter(CaseFilterQueryParam.FIRM_ID, v || undefined);
                      }}>
                      <MenuItem value="">Show All</MenuItem>
                      {firms.map((firm) => (
                        <MenuItem key={firm.id} value={firm.id.toString()}>
                          {firm.name}
                        </MenuItem>
                      ))}
                    </PSelect>
                  </FormControl>
                )}
                <FormControl size="small" sx={{ width: 500 }}>
                  <UserAutoComplete
                    options={optionsForUserAutocomplete}
                    dataPendo="case-table-filter-assignee"
                    label="Assignee"
                    textFieldProps={{
                      autoFocus: false,
                    }}
                    selected={selectedNamedUser}
                    onChange={(namedUser) => {
                      addFilter(CaseFilterQueryParam.ASSIGNEE_ID, namedUser?.id?.toString());
                    }}
                  />
                </FormControl>
                <FormControl size="small" sx={{ width: 350 }}>
                  <InputLabel variant="outlined">Case Status</InputLabel>
                  <PSelect
                    data-pendo="case-table-filter-case-status"
                    label="Case Status"
                    value={caseFilterParams.caseStatus || ""}
                    onChange={(evt) => {
                      const v = evt.target.value as string;
                      addFilter(CaseFilterQueryParam.CASE_STATUS, v || undefined);
                    }}>
                    <MenuItem value="all">Show All</MenuItem>
                    <MenuItem value={CaseClientStatus.Active}>
                      Active ({clientLens?.casesOverview?.activeCount || 0})
                    </MenuItem>
                    <MenuItem value={CaseClientStatus.Inactive}>
                      Inactive ({clientLens?.casesOverview?.inactiveCount || 0})
                    </MenuItem>
                  </PSelect>
                </FormControl>
                <FormControl size="small" sx={{ width: 350 }}>
                  <InputLabel variant="outlined">Review Status</InputLabel>
                  <PSelect
                    data-pendo="case-table-filter-review-status"
                    label="Review Status"
                    multiple
                    value={caseFilterParams.reviewStatuses || ""}
                    onChange={(evt) => {
                      const selectedStatuses = evt.target.value as string[];
                      if (typeof selectedStatuses !== "object") return;
                      const paramName = CaseFilterQueryParam.REVIEW_STATUS;
                      const allSelected = setEquals(
                        new Set(selectedStatuses),
                        new Set(ALL_REVIEW_STATUSES.map((v) => v.value))
                      );
                      const noneSelected = selectedStatuses.length === 0;
                      if (noneSelected || allSelected || selectedStatuses.includes(showAllReviewStatusesValue)) {
                        addFilter(paramName, ["all"]);
                      } else {
                        addFilter(paramName, selectedStatuses);
                      }
                    }}>
                    <MenuItem value={showAllReviewStatusesValue}>Show All</MenuItem>
                    <MenuItem value="complete">
                      {shouldShowReviewStatusCounts
                        ? `Complete (${clientLens?.casesOverview?.completeCount || 0})`
                        : "Complete"}
                    </MenuItem>
                    <MenuItem value="new">
                      {shouldShowReviewStatusCounts
                        ? `Pending (${clientLens?.casesOverview?.newCount || 0})`
                        : "Pending"}
                    </MenuItem>
                    <MenuItem value="new_documents">
                      {shouldShowReviewStatusCounts
                        ? `Pending - New Documents (${clientLens?.casesOverview?.newDocumentsCount || 0})`
                        : "Pending - New Documents"}
                    </MenuItem>
                    <MenuItem value="no_documents">
                      {shouldShowReviewStatusCounts
                        ? `No Documents (${clientLens?.casesOverview?.noDocumentsCount || 0})`
                        : "No Documents"}
                    </MenuItem>
                  </PSelect>
                </FormControl>
              </>
            )}

            <IconButton
              onClick={(event) => setCaseDownloadMenuAnchorEl(event.currentTarget)}
              data-pendo="case-table-download-button">
              <Download />
            </IconButton>
            <Menu
              anchorEl={caseDownloadMenuAnchorEl}
              open={Boolean(caseDownloadMenuAnchorEl)}
              onClose={() => setCaseDownloadMenuAnchorEl(null)}>
              <FormControlLabel
                sx={{ ml: 1 }}
                label="Include Custom Properties"
                control={
                  <Checkbox
                    checked={includePropertiesCheckbox}
                    onChange={(event) => setIncludePropertiesCheckbox(event.target.checked)}></Checkbox>
                }></FormControlLabel>
              <FormControl>
                <Tooltip title={`Download ${casesTotal} ${casesTotal > 1 ? "cases" : "case"}`}>
                  <IconButton onClick={handleCaseDownloadClick}>
                    <FileDownloadDone />
                  </IconButton>
                </Tooltip>
              </FormControl>
            </Menu>
          </Box>
          <CaseTable
            cases={cases}
            pagination={{
              total: casesTotal,
              page: caseFilterParams.page,
              limit: caseFilterParams.rowsPerPage,
            }}
            loading={casesStatus === RequestStatus.PENDING}
            clientLens={clientLens}
            caseAssignees={assignees}
            refreshCases={() => fetchCases(caseFilterParams)}
            showFirms={enableFirmFeatures}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

interface ViewCasesProps {
  onClick: () => void;
}

export const ViewCases: FC<ViewCasesProps> = ({ onClick }) => {
  return (
    <ManagementCard
      title="View All Cases"
      onClick={onClick}
      Icon={ListAlt}
      color={theme.palette.primary.main}
      id="lens-admin-management-view-cases"
    />
  );
};

export default ViewCases;
